import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mountain, Phone, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TravelerLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: phone, 2: otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/traveler/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, name: name || undefined })
      });

      const data = await response.json();

      if (response.ok) {
        setStep(2);
        toast.success('OTP sent to your phone');
        // For testing - display OTP
        if (data.otp_for_testing) {
          toast(`Test OTP: ${data.otp_for_testing}`, { duration: 10000 });
        }
      } else {
        toast.error(data.detail || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/traveler/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, otp })
      });

      const data = await response.json();

      if (response.ok) {
        // Save token
        localStorage.setItem('traveler_token', data.token);
        localStorage.setItem('traveler', JSON.stringify(data.traveler));
        
        toast.success(data.is_new ? 'Welcome to GoHimalayan!' : 'Welcome back!');
        
        // Redirect to previous page or stays
        const redirectTo = new URLSearchParams(window.location.search).get('redirect') || '/stays';
        navigate(redirectTo);
      } else {
        toast.error(data.detail || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <span className="text-xl font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-[#2C5F8D] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              1
            </div>
            <div className={`w-16 h-1 rounded ${step >= 2 ? 'bg-[#2C5F8D]' : 'bg-gray-200'}`} />
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-[#2C5F8D] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              2
            </div>
          </div>

          {step === 1 ? (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome, Traveler!</h1>
                <p className="text-gray-500">Sign in to book amazing Himalayan experiences</p>
              </div>

              <form onSubmit={handleSendOTP}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name (optional)
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="What should we call you?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="9876543210"
                        className="pl-12"
                        maxLength={10}
                        data-testid="traveler-phone-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#2C5F8D] hover:bg-[#1e4570]"
                    disabled={loading || phone.length < 10}
                    data-testid="traveler-send-otp-btn"
                  >
                    {loading ? 'Sending...' : 'Get OTP'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Phone</h1>
                <p className="text-gray-500">Enter the 6-digit code sent to +91 {phone}</p>
              </div>

              <form onSubmit={handleVerifyOTP}>
                <div className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="text-center text-2xl tracking-widest h-14"
                      maxLength={6}
                      data-testid="traveler-otp-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#2C5F8D] hover:bg-[#1e4570]"
                    disabled={loading || otp.length !== 6}
                    data-testid="traveler-verify-otp-btn"
                  >
                    {loading ? 'Verifying...' : 'Verify & Continue'}
                    <CheckCircle className="w-5 h-5 ml-2" />
                  </Button>

                  <div className="flex items-center justify-between text-sm">
                    <button 
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                      <ArrowLeft className="w-4 h-4" /> Change number
                    </button>
                    <button 
                      type="button"
                      onClick={handleSendOTP}
                      className="text-[#2C5F8D] hover:underline"
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}

          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-sm text-gray-500">
              Are you a host?{' '}
              <Link to="/partner" className="text-[#2C5F8D] font-medium hover:underline">
                List your property
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelerLogin;
